import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { playbookBullets } from '../../mock/content';
import { useToast } from '../../hooks/use-toast';
import { track, events, emailDomain } from '../../lib/analytics';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Playbook = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '' });
  const [optIn, setOptIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [focus, setFocus] = useState({ name: false, email: false });

  const submit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim();
    if (!name || !email) {
      toast({ title: 'Please fill in both fields' });
      return;
    }
    if (!EMAIL_RE.test(email)) {
      toast({ title: 'Please enter a valid work email' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/leads`, {
        name,
        email,
        source: 'playbook',
        gdpr_consent: optIn,
      });
      track(events.PLAYBOOK_SUBMIT, {
        email_domain: emailDomain(email),
        gdpr_consent: optIn ? 'granted' : 'denied',
      });
      setForm({ name: '', email: '' });
      setOptIn(false);
      toast({
        title: 'Playbook on its way',
        description: 'Check your inbox in a moment.',
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({
        title: 'Something went wrong',
        description:
          typeof detail === 'string'
            ? detail
            : 'Please try again in a moment or email us directly.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = (active) =>
    `w-full h-11 bg-transparent border-b-2 text-[15px] text-navy placeholder:text-body-muted focus:outline-none transition-colors ${
      active ? 'border-brand-blue' : 'border-slate-200'
    }`;

  return (
    <section id="playbook" className="bg-white py-20 md:py-24">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-stretch">
          {/* Playbook cover — provided artwork */}
          <div className="relative rounded-2xl overflow-hidden shadow-mk-lg bg-navy-deep">
            <img
              src="https://customer-assets-v7afamib.emergentagent.net/job_kit-showcase-11/artifacts/j2hfmgxh_Screenshot%202026-08-17%20at%2010.57.44%E2%80%AFAM.png"
              alt="How Publishers Make Money in 2026 — Edition 01 · 5DBS Publisher Insights"
              className="w-full h-full object-cover block"
              loading="lazy"
            />
          </div>

          {/* Form side */}
          <div className="flex flex-col justify-center">
            <p className="text-brand-blue text-[12px] font-bold tracking-[0.16em] uppercase mb-4">
              Free playbook
            </p>
            <h2 className="text-navy font-black tracking-[-0.01em] leading-[1.1] text-[32px] sm:text-[40px]">
              How publishers make money in 2026
            </h2>
            <p className="mt-5 text-body-muted text-[15.5px] leading-[1.7]">
              Seven practical plays for building a profitable lead generation line with audience extension — including the economics of the model, a worked margin example, battle-card templates for your sales team, and a 90-day launch plan.
            </p>
            <ul className="mt-6 space-y-3">
              {playbookBullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-brand-blue shrink-0" />
                  <span className="text-navy text-[15px] leading-[1.6] font-medium">{b}</span>
                </li>
              ))}
            </ul>

            <form onSubmit={submit} className="mt-8" data-testid="playbook-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <input
                  placeholder="Name"
                  data-testid="playbook-name-input"
                  value={form.name}
                  onFocus={() => setFocus((f) => ({ ...f, name: true }))}
                  onBlur={() => setFocus((f) => ({ ...f, name: false }))}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className={inputCls(focus.name)}
                />
                <input
                  placeholder="Work email"
                  type="email"
                  data-testid="playbook-email-input"
                  value={form.email}
                  onFocus={() => setFocus((f) => ({ ...f, email: true }))}
                  onBlur={() => setFocus((f) => ({ ...f, email: false }))}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className={inputCls(focus.email)}
                />
              </div>

              {/* GDPR-compliant opt-in — unticked by default */}
              <label className="mt-5 flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  data-testid="playbook-gdpr-checkbox"
                  checked={optIn}
                  onChange={(e) => setOptIn(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-pink focus:ring-brand-pink cursor-pointer shrink-0"
                />
                <span className="text-body-muted text-[13px] leading-[1.55]">
                  I'd like to receive occasional publisher revenue insights from 5DBS. Optional — you can unsubscribe from every email.
                </span>
              </label>

              <button
                type="submit"
                data-testid="playbook-submit-button"
                disabled={submitting}
                className="mt-6 inline-flex items-center justify-center h-11 px-7 rounded-md bg-brand-pink hover:bg-brand-pink/90 text-white text-[13px] font-bold uppercase tracking-[0.08em] shadow-mk hover:shadow-mk-lg hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60"
              >
                {submitting ? 'Sending…' : 'Send me the playbook'}
              </button>
              <p className="mt-3 text-body-muted text-[12.5px] leading-[1.55]">
                We'll only use your email to send the playbook{optIn ? ' and the insights you opted into' : ''}. See our{' '}
                <Link to="/privacy" className="text-brand-blue font-semibold hover:underline">
                  Privacy Policy
                </Link>
                {' '}for how we handle your data (GDPR / CCPA).
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Playbook;
